/* eslint-disable class-methods-use-this */
import 'intersection-observer'
import Events from '@utilities/events'

/**
 * @type {Window|Element}
 */
const SCROLL_ELEMENT = window

const OBSERVER_DEFAULT_OFFSET_Y = window.innerHeight * 0.2 || 150;
const OBSERVER_DEFAULT_OFFSET_X = 0
const OBSERVER_DEFAULT_THRESHOLD = buildThresholdList()

const INVIEW_JS_HOOK = '[js-hook-inview]'
const INVIEW_TRIGGERS_HOOK = 'data-inview-trigger'
const INVIEW_OUTVIEW_CLASS = 'is--out-view'
const INVIEW_THRESHOLD_HOOK = 'data-inview-threshold'
const INVIEW_PERSISTENT_HOOK = 'data-inview-persistent'
const PD_EDIT_MODE_CLASS = '.is--page-designer-edit-mode'

const CONFIG = {
  rootMargin: `${OBSERVER_DEFAULT_OFFSET_Y}px ${OBSERVER_DEFAULT_OFFSET_X}px`,
  threshold: OBSERVER_DEFAULT_THRESHOLD,
}

class InView {
  constructor() {
    this.nodes = getNodes()
    this.observer = null
    this.setObserver()
    this.bindEvents()
    this.setupDOMObserver()
  }

  bindEvents() {
    Events.$on('in-view::update', (_event, data) => {
      const elements = data && data.elements ? data.elements : undefined
      const hook = data && data.hook ? data.hook : undefined
      this.addElements(elements, hook)
    })
  }

  /**
   * Setup MutationObserver to detect new [js-hook-inview] elements added to the DOM
   * This is needed for Page Designer components loaded dynamically
   */
  setupDOMObserver() {
    // Only run in Page Designer edit mode
    if (!isPageDesigner() || !window.MutationObserver) return;

    this.domObserver = new MutationObserver((mutations) => {
      let hasNewElements = false;

      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            // Check if the added node has js-hook-inview or contains such elements
            if (node.nodeType === 1) {
              const isInViewElement = node.matches && node.matches(INVIEW_JS_HOOK);
              const hasInViewElements = node.querySelectorAll && node.querySelectorAll(INVIEW_JS_HOOK).length > 0;

              if (isInViewElement || hasInViewElements) {
                hasNewElements = true;
              }
            }
          });
        }
      });

      // Update elements if new ones were added
      if (hasNewElements) {
        const newElements = getNodes();
        this.addElements(newElements);
      }
    });

    // Observe the entire document for added inview nodes
    if (document.body) {
      this.domObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        this.domObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      });
    }
  }

  setObserver() {
    this.observer = new IntersectionObserver(this.onObserve.bind(this), CONFIG)
    this.bindObservedNodes()
  }

  bindObservedNodes() {
    this.nodes.forEach(node => {
      if (!node.__inviewTriggerHook) {
        node.__inviewTriggerHook = node.getAttribute(INVIEW_TRIGGERS_HOOK)
      }
      if (!node.__inviewPersistent) {
        node.__inviewPersistent = node.getAttribute(INVIEW_PERSISTENT_HOOK) === 'true'
      }
      if (!node.__inviewThreshold) {
        const attribute = node.getAttribute(INVIEW_THRESHOLD_HOOK)
        node.__inviewThreshold = attribute ? parseFloat(attribute) || false : false
      }
      if (!node.__inviewInitialized && this.observer) {
        this.observer.observe(node)
      }
      if (!node.__inviewInitialized) {
        node.__inviewInitialized = true
      }
    })
  }

  onObserve(entries) {
    entries.forEach(entry => this.whenElementInViewport(entry, this.observer))
  }

  whenElementInViewport(entry, observer) {
    const triggers = entry.target.__inviewTriggerHook
    const element = entry.target

    element.inviewProperties = calculateInviewProperties(entry)

    if (
      // Element is past bottom of the screen
      element.inviewProperties.scrolledPastViewport.bottom &&
      (element.inviewProperties.scrolledPastViewport.left ||
        element.inviewProperties.scrolledPastViewport.right) &&
      // Element does not have a threshold or it has a threshold and the threshold is met
      (!element.__inviewThreshold ||
        (element.__inviewThreshold && element.__inviewThreshold <= entry.intersectionRatio))
    ) {
      element.classList.remove(INVIEW_OUTVIEW_CLASS)
      triggerEvents(getTriggers(triggers), element)

      if (!element.__inviewPersistent) observer.unobserve(entry.target)
    } else {
      element.classList.add(INVIEW_OUTVIEW_CLASS)

      if (
        element.__inviewPersistent &&
        (element.inviewProperties.scrolledPastViewport.left ||
          element.inviewProperties.scrolledPastViewport.right)
      )
        triggerEvents(getTriggers(triggers), element)
    }
  }

  addElements(elements = getNodes(), hook = false) {
    elements.forEach(element => {
      if (element.__inviewInitialized) {
        return
      }
      if (hook) {
        element.__inviewTriggerHook = hook
      }

      this.nodes.push(element)

      this.bindObservedNodes()
    })
  }
}

function triggerEvents(triggers, data) {
  triggers.forEach(trigger => {
    Events.$trigger(trigger, { data })
  })
}

function getTriggers(triggers) {
  return triggers ? triggers.split(',') : []
}

/**
 * @typedef {Element & {
 *  __inviewTriggerHook: any
 *  __inviewPersistent: any
 *  __inviewThreshold: any
 *  __inviewInitialized: any
 * }} InViewElement
 * @return {InViewElement[]}
 */
function getNodes() {
  return Array.from(document.querySelectorAll(INVIEW_JS_HOOK))
}

/**
 * Checks if given element is in viewport
 * @param {Object} entry Intersection observer entry
 */
function calculateInviewProperties(entry) {
  let scrollTop
  let scrollLeft

  if (SCROLL_ELEMENT instanceof Window) {
    scrollTop = SCROLL_ELEMENT.pageYOffset || document.documentElement.scrollTop
    scrollLeft = SCROLL_ELEMENT.pageXOffset || document.documentElement.scrollLeft
  } else {
    scrollTop = SCROLL_ELEMENT.scrollTop || document.documentElement.scrollTop
    scrollLeft = SCROLL_ELEMENT.scrollLeft || document.documentElement.scrollLeft
  }

  const { top, bottom, left, right } = getElementOffset(entry)
  const position = { top, bottom, left, right }

  // Fallback to viewport size if rootBounds is unavailable (polyfill or browser quirk)
  const rootHeight = entry.rootBounds ? entry.rootBounds.height : window.innerHeight
  const rootWidth = entry.rootBounds ? entry.rootBounds.width : window.innerWidth

  const inViewDirections = getInViewDirections({
    entry,
    position,
    rootHeight,
    rootWidth,
    scrollTop,
    scrollLeft,
  })

  return inViewDirections
}

/**
 * Returns the offsetTop and offsetLeft of given element
 * @param {Object} entry
 * @returns {Object} Object of top, bottom, left and right position
 */
function getElementOffset(entry) {
  let targetElement = entry.target
  const elementStyles = window.getComputedStyle(targetElement)

  const margin = {}
  margin.top = (elementStyles.marginTop && parseInt(elementStyles.marginTop, 10) / 2) || 0
  margin.right = (elementStyles.marginRight && parseInt(elementStyles.marginRight, 10) / 2) || 0
  margin.bottom = (elementStyles.marginBottom && parseInt(elementStyles.marginBottom, 10) / 2) || 0
  margin.left = (elementStyles.marginLeft && parseInt(elementStyles.marginLeft, 10) / 2) || 0

  let top = 0 + margin.top + margin.bottom
  let left = 0 + margin.left + margin.right

  do {
    top += targetElement.offsetTop || 0
    left += targetElement.offsetLeft || 0
    targetElement = targetElement.offsetParent
  } while (targetElement)

  return {
    top,
    left,
    right: left + entry.boundingClientRect.width,
    bottom: top + entry.boundingClientRect.height,
  }
}

/**
 * Get matching in view directions
 * @param {Object} options
 * @returns {Object} matches
 */
function getInViewDirections(options) {
  const { intersectionRatio } = options.entry
  const { width, height } = options.entry.boundingClientRect

  const topPosition = options.entry.boundingClientRect.top
  const bottomPosition = options.entry.boundingClientRect.bottom
  const leftPosition = options.entry.boundingClientRect.left
  const rightPosition = options.entry.boundingClientRect.right

  const scrolledPastViewport = {}

  const isVisible = elementIsVisible(width, height)

  scrolledPastViewport.top = topPosition + height < 0 && isVisible
  scrolledPastViewport.bottom = topPosition <= options.rootHeight && isVisible
  scrolledPastViewport.right = leftPosition <= options.rootWidth && isVisible
  scrolledPastViewport.left = leftPosition <= 0 && isVisible

  const isInViewport = {
    horizontal:
      options.entry.isIntersecting && (scrolledPastViewport.left || scrolledPastViewport.right),
    vertical:
      options.entry.isIntersecting && (scrolledPastViewport.top || scrolledPastViewport.bottom),
  }

  return {
    position: {
      top: topPosition,
      right: rightPosition,
      bottom: bottomPosition,
      left: leftPosition,
    },
    scrolledPastViewport,
    isInViewport,
    height,
    width,
    intersectionRatio,
    windowHeight: window.innerHeight,
    windowWidth: window.innerWidth,
  }
}

function isPageDesigner() {
  return Boolean(document.querySelector(PD_EDIT_MODE_CLASS))
}

function buildThresholdList() {
  const numSteps = 1000
  const thresholds = []

  for (let i = 1.0; i <= numSteps; i++) {
    const ratio = i / numSteps
    thresholds.push(ratio)
  }

  thresholds.push(0)
  return thresholds
}

function elementIsVisible(width, height) {
  return width > 0 || height > 0
}

export default new InView()
